import { ReactNode } from 'react';
import { AutocompleteInputChangeReason, SxProps, Theme } from '@mui/material';
import { DateView } from '@mui/x-date-pickers';
import { Item } from '@components/select/Select';
import { TypeAheadFilterProps } from '@components/table/filters/TypeAheadFilter';
import { DataType, UIFieldType } from '@lio/api-types';

export type materialUiValidWidth = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 'auto';

export enum FormFieldType {
  DatePickerField,
  TextField,
  RichTextEditor,
  PhoneNumberField,
  SelectField,
  FilteredSelectField,
  RadioGroupField,
  CheckboxField,
  ToggleSwitchField,
  DateTimeRangeField,
  Label,
  MultiSelectField,
  CustomField,
  // FUTURE: add others
}

export interface TFormField {
  /** name of field / form key */
  name: string;
  fieldType: FormFieldType;
  label: string;
  placeholder?: string;
  /** @deprecated - use watchedFields instead */
  watchedField?: string;
  watchedFields?: string[];
  isVisible?: boolean | (({ data }: { data: any }) => boolean);
  /** Helper text to always show on the field */
  helperText?: string | ReactNode;
  hoverText?: string;
  error?: boolean;
  tooltipTitle?: string;
  defaultValue?: any;
  /** Optional variant, @default 'outlined' */
  variant?: string;
  disabled?: boolean | (({ data }: { data: any }) => boolean);
  required?: boolean | (({ data }: { data: any }) => boolean);
  gridWidth: {
    xs?: materialUiValidWidth;
    sm?: materialUiValidWidth;
    md?: materialUiValidWidth;
    lg?: materialUiValidWidth;
  };
  /** If true field will be auto focused */
  autoFocus?: boolean;
  sx?: SxProps<Theme>;
  fieldConfig?: {
    /** Fields used for helping with subjectivity context */
    uiFieldType?: UIFieldType;
    dataType?: DataType;
    /** --- end --- */
    requireNewLine?: boolean;
    // FUTURE things to consider/add as needed (leaving for references)
    textField?: {
      startIcon?: ReactNode;
      endIcon?: ReactNode;
      multiline?: boolean;
      autocomplete?: 'on' | 'off' | 'new-password';
      rows?: number;
      handleOnPattern?: (str: string) => string;
      regexPattern?: RegExp;
      regexMessage?: string;
      inputType?: string;
      //   inputMask?: string;
      //   minLength?: number;
      maxLength?: number;
      handleDebouncedInputChange?: (value: string) => void;
    };
    selectField?: {
      options?: Item[];
      handleOnScroll?: (e: any) => void;
      handleInputChange?: (value: string, reason: AutocompleteInputChangeReason) => void;
      menuProps?: { PaperProps?: { sx?: SxProps }; sx?: SxProps };
      validate?: (value: string, formValues?: Record<string, unknown>) => boolean | string;
    };
    dateField?: {
      minDate?: Date;
      maxDate?: Date;
      openTo?: DateView;
    };
    radioField?: {
      layout?: 'horizontal' | 'vertical';
      options?: Item[];
    };
    labelField?: {
      sxProps?: {
        box?: SxProps;
        value?: SxProps;
        label?: SxProps;
      };
    };
    checkboxField?: {
      fontWeight?: 'normal' | 'bold';
      CustomLabelComponent?: React.ReactNode;
    };
    switchField?: {
      variant?: 'primary' | 'secondary';
      size?: 'small' | 'medium';
    };
    dateTimeRangeField?: {
      startDate: {
        name: string;
        minDate: Date;
      };
      startTime: {
        name: string;
        default?: Date;
        disabled?: boolean;
        required?: boolean;
      };
      endDate: {
        label: string;
        name: string;
        default?: Date;
        disabled?: boolean;
        required?: boolean;
      };
      endTime: {
        required?: boolean;
        name: string;
        default?: Date;
        disabled?: boolean;
      };
    };
    multiSelectField?: {
      options?: TypeAheadFilterProps['options'];
      limitTags?: number;
      onInputChange?: (event: any, value: string) => void;
      loading?: boolean;
    };
    customField?: {
      Component: React.ElementType;
    };
  };
}
